import {
  describeMysteryNumber,
  mysteryRelation,
  mysteryTotal,
  type MysteryNumberState,
} from "@seeing-maths/math-models";

export interface MysteryNumberArtifactProps {
  state: MysteryNumberState;
  equation?: string;
  interactive?: boolean;
  revealValue?: boolean;
  showBars?: boolean;
  remediation?: string | null;
  onValueChange?: (value: number) => void;
}

function equationFor(state: MysteryNumberState, reveal: boolean): string {
  const box = reveal ? String(state.unknown) : "□";
  const unknowns = Array.from({ length: state.coefficient }, () => box).join(" + ");
  const left = [state.known > 0 ? String(state.known) : "", unknowns]
    .filter(Boolean)
    .join(" + ");
  return `${left} = ${state.target}`;
}

function remediationCopy(code: string | null | undefined, state: MysteryNumberState) {
  if (code === "MN-COMBINE-ALL") {
    return `The whole is already ${state.target}. Start at ${state.known}; the unfilled part is what the box must hold.`;
  }
  if (code === "MN-DIFFERENCE-BACKWARDS") {
    return `Walk from ${state.known} up to ${state.target}. The distance is positive because the box adds to ${state.known}.`;
  }
  if (code === "MN-IGNORE-RELATION") {
    return `Test the complete left side each time. It must become exactly ${state.target}.`;
  }
  return null;
}

export function MysteryNumberArtifact({
  state,
  equation,
  interactive = false,
  revealValue = false,
  showBars = true,
  remediation,
  onValueChange,
}: MysteryNumberArtifactProps) {
  const total = mysteryTotal(state);
  const relation = mysteryRelation(state);
  const maximum = Math.max(total, state.target, 1);
  const relationLabel = describeMysteryNumber(state);
  const help = remediationCopy(remediation, state);

  return (
    <section className={`math-artifact relation-${relation}`} aria-label="Mystery number relationship">
      <div className="artifact-equation" aria-label={equation ?? equationFor(state, revealValue)}>
        {equation ?? equationFor(state, revealValue)}
      </div>

      {showBars ? (
        <div className="value-bars" aria-label={relationLabel}>
          <div className="value-bar-row">
            <span>Left side</span>
            <strong>{total}</strong>
            <div className="value-track">
              <span
                className="value-fill value-fill-left"
                style={{ width: `${(total / maximum) * 100}%` }}
              />
            </div>
          </div>
          <div className="value-bar-row">
            <span>Right side</span>
            <strong>{state.target}</strong>
            <div className="value-track">
              <span
                className="value-fill value-fill-right"
                style={{ width: `${(state.target / maximum) * 100}%` }}
              />
            </div>
          </div>
        </div>
      ) : (
        <div className="artifact-silhouette" aria-hidden="true">
          <span className="silhouette-known">{state.known || "same"}</span>
          {Array.from({ length: state.coefficient }, (_, index) => (
            <span className="silhouette-box" key={index}>
              {revealValue ? state.unknown : "□"}
            </span>
          ))}
          <span className="silhouette-target">{state.target}</span>
        </div>
      )}

      {interactive ? (
        <div className="artifact-controls" aria-label="Set the mystery number">
          <button
            className="btn btn-secondary btn-icon artifact-step"
            type="button"
            aria-label="Make the mystery number one smaller"
            disabled={state.locked || state.unknown <= state.min}
            onClick={() => onValueChange?.(state.unknown - 1)}
          >
            −
          </button>
          <label className="artifact-slider-label">
            <span className="sr-only">Mystery number, {state.unknown}</span>
            <input
              aria-label={`Mystery number, currently ${state.unknown}`}
              type="range"
              min={state.min}
              max={state.max}
              value={state.unknown}
              disabled={state.locked}
              onChange={(event) => onValueChange?.(Number(event.currentTarget.value))}
            />
            <span className="artifact-output" aria-hidden="true">
              {state.unknown}
            </span>
          </label>
          <button
            className="btn btn-secondary btn-icon artifact-step"
            type="button"
            aria-label="Make the mystery number one larger"
            disabled={state.locked || state.unknown >= state.max}
            onClick={() => onValueChange?.(state.unknown + 1)}
          >
            +
          </button>
        </div>
      ) : null}

      {help ? <p className="artifact-remediation">{help}</p> : null}
    </section>
  );
}
